import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import Modal from 'components/Modal';
import { Form, Input, Spin } from 'antd';
import CurrencyInputPrecision from 'components/CurrencyInputPrecision';
import styled from 'styled-components';
import toast from 'utils/toast';
import settingApis from 'features/settingService/services/apis';
import { IAddOnItem, ICreateMultipleAddOnBody } from 'features/settingService/services/types/api';
import { IResponseDataBody } from 'services/response';
import get from 'lodash/get';
import { IServiceItemData } from 'services/shop/types/categories';
import Button from 'components/Button';
import TrashV2 from 'assets/Icons/trashV2';

type IServiceFormItem = {
  name: string;
  price: number;
};

type IFormData = {
  services: IServiceFormItem[];
};

type ModalAddNewServiceRef = {
  open: () => Promise<IServiceItemData[] | null>;
};

export const useModalAddMultiNewServicesRef = () => useRef<ModalAddNewServiceRef>(null);

const ModalAddMultiNewServices = forwardRef<ModalAddNewServiceRef>((_, ref) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const callBackRef = useRef<((data: IServiceItemData[] | null) => void) | null>(null);
  const [form] = Form.useForm<IFormData>();

  const handleClose = () => {
    setVisible(false);
    form.resetFields();
  };

  const handleOpen = () => {
    setVisible(true);
  };

  useImperativeHandle(ref, () => ({
    open: async () => {
      handleOpen();
      return new Promise<IServiceItemData[] | null>((resolve) => {
        callBackRef.current = resolve;
      });
    },
  }));

  const onFinish = async (values: IFormData) => {
    try {
      setLoading(true);
      const body: ICreateMultipleAddOnBody[] = values.services.map((v) => ({
        itemName: v.name,
        price: v.price,
      }));

      const res: IResponseDataBody<IAddOnItem[]> = await settingApis.createMultipleAddOn(body);
      if (res.data.data) {
        callBackRef.current?.(
          res.data.data.map((item) => ({
            id: item.id,
            serviceName: item.itemName,
            priceSell: item.price,
            catId: '',
            duration: 0,
            priceCost: 0,
            commission: 0,
          })),
        );
        toast.success('Add new services successfully');
        handleClose();
      } else {
        toast.error('Failed to add new service');
        callBackRef.current?.(null);
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg || 'Failed to add new service');
      callBackRef.current?.(null);
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <Modal
      modalTitle={'Add New Service'}
      visible={visible}
      onClose={handleClose}
      onSubmit={() => form.submit()}
      width={'1080px'}
      noneBodyStyle
      noneFooterStyle
      footerPadding={0.5}
      okTitle='OK'
      v2
    >
      <Spin spinning={loading}>
        <Container>
          <Form
            layout='vertical'
            form={form}
            onFinish={onFinish}
            initialValues={{
              services: [{ name: '', price: undefined }],
            }}
          >
            <Form.List name='services'>
              {(fields, { add, remove }) => (
                <>
                  <RowHeader>
                    <div>NAME</div>
                    <div>PRICE</div>
                  </RowHeader>
                  {fields.map((field, index) => (
                    <ServiceRow key={field.key}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'name']}
                        rules={[
                          {
                            required: true,
                            message: 'Please enter service name',
                          },
                        ]}
                      >
                        <Input placeholder='Please enter...' />
                      </Form.Item>

                      <Form.Item
                        {...field}
                        name={[field.name, 'price']}
                        rules={[
                          {
                            required: true,
                            message: 'Please enter price',
                          },
                        ]}
                      >
                        <CurrencyInputPrecision size='small' />
                      </Form.Item>

                      <DeleteButton
                        onClick={() => {
                          if (fields.length === 1) return;
                          remove(field.name);
                        }}
                      >
                        <TrashV2 fill='#232F3E' />
                      </DeleteButton>
                    </ServiceRow>
                  ))}
                  <Button
                    ntype='DARK_BLUE'
                    onClick={() =>
                      add({
                        name: '',
                        price: undefined,
                      })
                    }
                    height={3}
                    className='cardCashier'
                    icon='addWhite'
                  >
                    Add New
                  </Button>
                </>
              )}
            </Form.List>
          </Form>
        </Container>
      </Spin>
    </Modal>
  );
});

ModalAddMultiNewServices.displayName = 'ModalAddMultiNewServices';
export default ModalAddMultiNewServices;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 16px;
`;

export const RowHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 180px 40px;
  gap: 16px;

  margin-bottom: 8px;

  font-size: 18px;
  font-weight: 600;
  color: #64748b;
`;

export const ServiceRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 180px 40px;
  gap: 16px;
  align-items: flex-start;

  margin-bottom: 16px;

  .ant-form-item {
    margin-bottom: 0;
  }
`;

export const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  &:hover {
    opacity: 0.7;
  }
`;
